import { jsPDF } from 'jspdf';

const STATUS_LABELS = {
  RASCUNHO: 'Rascunho',
  EM_ANALISE: 'Em análise',
  EM_FLUXO: 'Em fluxo',
  APROVADA: 'Aprovada',
  NEGADA: 'Negada',
  REPROVADA: 'Reprovada',
  EM_EXECUCAO: 'Em execução',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

const CI_TIPO_LABEL = {
  ADITIVO: 'Aditivo',
  CONTRATACAO: 'Contratação',
};

function escapeCsv(value) {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return String(dateString);
  return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

function nomesDestinatarios(item) {
  if (Array.isArray(item?.destinatarios) && item.destinatarios.length) {
    return item.destinatarios.map((d) => d?.nome).filter(Boolean).join('; ');
  }
  return item?.destinatario?.nome || '';
}

function tipoLabel(item) {
  if (item.tipo === 'CI') {
    const sub = CI_TIPO_LABEL[item.ciTipo] || item.ciTipo || '';
    return sub ? `CI · ${sub}` : 'CI';
  }
  if (item.tipo === 'NOTIFICACAO') {
    return item.subtipo ? `Notificação · ${item.subtipo}` : 'Notificação';
  }
  if (item.tipo === 'TR') return 'TR';
  return item.tipo || '';
}

function rowData(item) {
  const contrato = item.contrato
    ? [item.contrato.numero, item.contrato.empresa].filter(Boolean).join(' · ')
    : '';
  return {
    codigo: item.codigo || '',
    tipo: tipoLabel(item),
    titulo: item.titulo || '',
    unidade: item.unidade || '',
    status: STATUS_LABELS[item.status] || item.status || '',
    destinatarios: nomesDestinatarios(item),
    contrato,
    criadoEm: formatDateTime(item.createdAt),
  };
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/**
 * Exporta listagem filtrada para CSV (abre no Excel).
 */
export function exportSolicitacoesExcel(solicitacoes, { categoriaLabel = 'Solicitacoes' } = {}) {
  const rows = (solicitacoes || []).map(rowData);
  const headers = [
    'Código',
    'Tipo',
    'Assunto',
    'Unidade',
    'Status',
    'Destinatário(s)',
    'Contrato',
    'Criado em',
  ];
  const lines = [
    headers.join(','),
    ...rows.map((r) =>
      [
        r.codigo,
        r.tipo,
        r.titulo,
        r.unidade,
        r.status,
        r.destinatarios,
        r.contrato,
        r.criadoEm,
      ]
        .map(escapeCsv)
        .join(',')
    ),
  ];
  // BOM para Excel reconhecer UTF-8
  const csv = `\uFEFF${lines.join('\r\n')}`;
  const safe = String(categoriaLabel || 'Solicitacoes').replace(/[<>:"/\\|?*]/g, '_');
  const stamp = new Date().toISOString().slice(0, 10);
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `solicitacoes_${safe}_${stamp}.csv`);
  return rows.length;
}

/**
 * Exporta listagem filtrada para PDF (jspdf).
 */
export function exportSolicitacoesPdf(solicitacoes, { categoriaLabel = 'Solicitações', unidade = '' } = {}) {
  const rows = (solicitacoes || []).map(rowData);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const margin = 10;
  let y = 14;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`Solicitações — ${categoriaLabel}`, margin, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const meta = [
    unidade ? `Unidade: ${unidade}` : null,
    `Gerado em: ${new Date().toLocaleString('pt-BR')}`,
    `Registros: ${rows.length}`,
  ]
    .filter(Boolean)
    .join('  ·  ');
  doc.text(meta, margin, y);
  y += 8;

  const cols = [
    { key: 'codigo', label: 'Código', w: 28 },
    { key: 'tipo', label: 'Tipo', w: 32 },
    { key: 'titulo', label: 'Assunto', w: 70 },
    { key: 'unidade', label: 'Unidade', w: 22 },
    { key: 'status', label: 'Status', w: 26 },
    { key: 'destinatarios', label: 'Destinatário(s)', w: 40 },
    { key: 'contrato', label: 'Contrato', w: 35 },
    { key: 'criadoEm', label: 'Criado em', w: 28 },
  ];

  const drawHeader = () => {
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, y - 4, 277, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    let x = margin;
    cols.forEach((c) => {
      doc.text(c.label, x + 1, y);
      x += c.w;
    });
    y += 6;
    doc.setFont('helvetica', 'normal');
  };

  drawHeader();

  rows.forEach((r, idx) => {
    if (y > 190) {
      doc.addPage();
      y = 14;
      drawHeader();
    }
    if (idx % 2 === 0) {
      doc.setFillColor(248, 250, 255);
      doc.rect(margin, y - 3.5, 277, 6.5, 'F');
    }
    let x = margin;
    cols.forEach((c) => {
      const text = String(r[c.key] || '—');
      const clipped = doc.splitTextToSize(text, c.w - 2)[0] || '—';
      doc.text(clipped, x + 1, y);
      x += c.w;
    });
    y += 6.5;
  });

  if (!rows.length) {
    doc.setFontSize(10);
    doc.text('Nenhum registro para exportar.', margin, y + 4);
  }

  const safe = String(categoriaLabel || 'Solicitacoes').replace(/[<>:"/\\|?*]/g, '_');
  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`solicitacoes_${safe}_${stamp}.pdf`);
  return rows.length;
}
